/*
  The Brane and Loop panels as a process rather than a picture. Each is one
  scripted run, a function of the milliseconds since it started, so the view
  holds no state of its own beyond the clock and a test can read any moment of
  it. A run is cut into steps, the beats a person goes through, and the panel
  names the one it is on; the view can start the clock from any of them.

  Brane: you make one (its name and bio typed, Save pressed) and share it (its
  link appears, Copy link is pressed). Someone opens the link: the card as
  /b/<handle> shows it, Send pressed, their message typed and sent. The String
  it opened, as you see it, the replies arriving.

  Loop: a new Loop is made (its topic typed, anonymous switched on, Create
  pressed), the room opens with other people's messages arriving in it, and
  once the talk dies down it is archived, still there to read.
*/

const CHAR_MS = 32;
/** The bio is longer than the name and types faster, so the run does not wait on it. */
const QUICK_MS = 14;
const LINE_MS = 1450;

export interface Run<F> {
  /** The whole run, ms. */
  length: number;
  /** Where each step starts, ms; the first is 0. */
  steps: number[];
  at: (t: number) => F;
}

/** How many of `len` characters are typed at `t`, typing from `from` at `per` ms a character. */
const typed = (t: number, from: number, len: number, per = CHAR_MS) => Math.max(0, Math.min(len, Math.floor((t - from) / per)));

/** How many of `lines` lines have arrived at `t`, the first at `from`. */
const arrived = (t: number, from: number, lines: number) => (t < from ? 0 : Math.min(lines, Math.floor((t - from) / LINE_MS) + 1));

/** Which step `t` is in. */
export function stepAt(steps: readonly number[], t: number): number {
  let i = 0;
  while (i + 1 < steps.length && t >= steps[i + 1]) i++;
  return i;
}

export interface BraneFrame {
  scene: "make" | "card" | "write" | "chat";
  /** How many characters of the Brane's name and bio are typed. */
  name: number;
  bio: number;
  /** Save is pressed: the Brane exists, and its link with it. */
  saved: boolean;
  /** Copy link is pressed. */
  copied: boolean;
  /** Send is pressed on the card, and again in the composer. */
  pressed: boolean;
  /** How many characters of the message are typed. */
  typed: number;
  /** How many of the chat's lines have arrived. */
  lines: number;
}

/** The Brane run, for a name, a bio and a first message of these lengths and a chat of `lines` lines. */
export function braneRun(nameLen: number, bioLen: number, msgLen: number, lines: number): Run<BraneFrame> {
  const nameAt = 500;
  const bioAt = nameAt + nameLen * CHAR_MS + 250;
  const save = bioAt + bioLen * QUICK_MS + 450;
  const share = save + 350;
  const copy = share + 900;
  const card = copy + 1500;
  const press = card + 1500;
  const write = card + 1850;
  const typeAt = write + 450;
  const send = typeAt + msgLen * CHAR_MS + 500;
  const chat = send + 450;
  const length = chat + (lines - 1) * LINE_MS + 1600;
  return {
    length,
    steps: [0, share, card, chat],
    at(t) {
      const base = { name: nameLen, bio: bioLen, saved: true, copied: true, pressed: false, typed: 0, lines: 0 };
      if (t < card) {
        return {
          ...base,
          scene: "make",
          name: typed(t, nameAt, nameLen),
          bio: typed(t, bioAt, bioLen, QUICK_MS),
          saved: t >= save,
          copied: t >= copy,
        };
      }
      if (t < write) return { ...base, scene: "card", pressed: t >= press };
      if (t < chat) return { ...base, scene: "write", typed: typed(t, typeAt, msgLen), pressed: t >= send };
      return { ...base, scene: "chat", typed: msgLen, lines: arrived(t, chat, lines) };
    },
  };
}

export interface LoopFrame {
  scene: "create" | "room";
  /** How many characters of the topic are typed. */
  typed: number;
  /** Anonymous is switched on. */
  anonymous: boolean;
  /** Create is pressed. */
  pressed: boolean;
  /** How many of the room's messages have arrived. */
  lines: number;
  /** The talk has died down and the Loop is archived: read-only, still all there. */
  archived: boolean;
}

/** The Loop run, for a topic of `len` characters and a room of `lines` messages. */
export function loopRun(len: number, lines: number): Run<LoopFrame> {
  const typeAt = 900;
  const toggle = typeAt + len * CHAR_MS + 500;
  const press = toggle + 800;
  const room = press + 450;
  const quiet = room + (lines - 1) * LINE_MS + 1900;
  const length = quiet + 2600;
  return {
    length,
    steps: [0, room, quiet],
    at(t) {
      if (t < room) {
        return { scene: "create", typed: typed(t, typeAt, len), anonymous: t >= toggle, pressed: t >= press, lines: 0, archived: false };
      }
      return { scene: "room", typed: len, anonymous: true, pressed: false, lines: arrived(t, room, lines), archived: t >= quiet };
    },
  };
}
